/**
 * One way to create a Track.
 *
 * Before this helper, ~16 call sites across ~13 files called
 * `useTrackStore.getState().createTrack()` (or the hook form) directly, each
 * re-deciding for itself whether to look at the returned Track, the store's
 * `error` field, or nothing at all — several call sites silently discarded a
 * failed create. `createTrack` itself never throws and never rejects: a
 * denial or a DB error both come back as `null` with a message left on
 * `trackStore.error`.
 *
 * Every interactive call site should route through `createTrackWithFeedback`
 * so a failure always surfaces the real reason (a policy denial, a duplicate
 * default-track constraint, a dead scope reference, ...) instead of just
 * failing silently or showing a generic "Failed to create Track".
 *
 * The two programmatic *batch* callers — launchCycleDesigner.ts and
 * baselineTrackMaterializationService.ts, which create many tracks in a loop
 * as part of a larger operation — stay on the store directly and collect the
 * store's `error` per track themselves; a toast per track in a 20-track batch
 * is not useful feedback.
 */
import { useTrackStore } from '../../stores/prioritization/trackStore';
import type { CreateTrackInput, Track } from '../../types/prioritization/track';

/** Matches ToastContextValue['showToast'] (src/components/ui/Toast.tsx) without importing the .tsx provider into this lib module. */
export type CreateTrackToastFn = (
  type: 'success' | 'error' | 'warning' | 'info',
  title: string,
  message?: string,
  duration?: number,
) => void;

export interface CreateTrackFeedbackOptions {
  /** When provided, a failed create also raises an error toast with the real reason. */
  toast?: CreateTrackToastFn;
  /** Toast title on failure. Defaults to "Couldn't create track". */
  toastTitle?: string;
}

export interface CreateTrackFeedbackResult {
  ok: boolean;
  trackId?: string;
  track?: Track;
  error?: string;
}

/**
 * Create a Track and normalize the result so every caller shows the real
 * reason on failure. `trackStore.createTrack` already converts policy
 * denials and DB errors into a human-readable string before setting
 * `error` — this helper just makes forwarding that string to the UI the
 * path of least resistance.
 */
export async function createTrackWithFeedback(
  input: CreateTrackInput,
  options: CreateTrackFeedbackOptions = {},
): Promise<CreateTrackFeedbackResult> {
  const track = await useTrackStore.getState().createTrack(input);

  if (track) {
    return { ok: true, trackId: track.id, track };
  }

  const error = useTrackStore.getState().error || 'Failed to create track.';
  options.toast?.('error', options.toastTitle ?? "Couldn't create track", error);
  return { ok: false, error };
}
